/** Local preferences for remote image loading (Ustav §17 / C-22). */
const ALWAYS_KEY = 'mail.allowImages.from.v1'
const SESSION_MSG_KEY = 'mail.allowImages.msg.v1'
const SESSION_FROM_KEY = 'mail.allowImages.fromSession.v1'

/** Normalize `Name <email@x>` or bare email → lowercase address. */
export function normalizeSenderEmail(raw: string | null | undefined): string {
  if (!raw) return ''
  const s = raw.trim().toLowerCase()
  const angle = s.match(/<([^>\s]+@[^>\s]+)>/)
  if (angle) return angle[1].trim()
  if (s.includes('@') && !s.includes(' ')) return s
  const loose = s.match(/([^\s<>]+@[^\s<>]+)/)
  return loose ? loose[1].trim() : s
}

function readAlways(): Record<string, true> {
  try {
    const raw = localStorage.getItem(ALWAYS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const out: Record<string, true> = {}
    for (const [k, v] of Object.entries(parsed)) {
      if (v) out[normalizeSenderEmail(k) || k.toLowerCase()] = true
    }
    return out
  } catch {
    return {}
  }
}

function readSessionMap(key: string): Record<string, true> {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const out: Record<string, true> = {}
    for (const [k, v] of Object.entries(parsed)) {
      if (v && k) out[k] = true
    }
    return out
  } catch {
    return {}
  }
}

function writeSessionMap(key: string, map: Record<string, true>): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(map))
  } catch {
    /* private mode / quota — ignore */
  }
}

export function isSenderImagesAlwaysAllowed(email: string | null | undefined): boolean {
  const key = normalizeSenderEmail(email)
  if (!key) return false
  return Boolean(readAlways()[key])
}

export function allowSenderImagesAlways(email: string): void {
  const key = normalizeSenderEmail(email)
  if (!key) return
  const map = readAlways()
  map[key] = true
  try {
    localStorage.setItem(ALWAYS_KEY, JSON.stringify(map))
  } catch {
    /* ignore */
  }
  // Also session so same-tab refresh is consistent even if LS is blocked
  allowSenderImagesSession(key)
}

export function revokeSenderImagesAlways(email: string): void {
  const key = normalizeSenderEmail(email)
  const map = readAlways()
  delete map[key]
  try {
    localStorage.setItem(ALWAYS_KEY, JSON.stringify(map))
  } catch {
    /* ignore */
  }
}

/** Per-message opt-in — survives F5 in the same tab (sessionStorage). */
export function isMessageImagesSessionAllowed(messageId: string | null | undefined): boolean {
  if (!messageId) return false
  return Boolean(readSessionMap(SESSION_MSG_KEY)[messageId])
}

export function allowMessageImagesSession(messageId: string): void {
  if (!messageId) return
  const map = readSessionMap(SESSION_MSG_KEY)
  map[messageId] = true
  writeSessionMap(SESSION_MSG_KEY, map)
}

/** Sender opt-in for this browser tab session (survives F5, cleared when tab closes). */
export function isSenderImagesSessionAllowed(email: string | null | undefined): boolean {
  const key = normalizeSenderEmail(email)
  if (!key) return false
  return Boolean(readSessionMap(SESSION_FROM_KEY)[key])
}

export function allowSenderImagesSession(email: string): void {
  const key = normalizeSenderEmail(email)
  if (!key) return
  const map = readSessionMap(SESSION_FROM_KEY)
  map[key] = true
  writeSessionMap(SESSION_FROM_KEY, map)
}
